import React from 'react';
import { observer } from 'mobx-react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { withAuth0 } from '@auth0/auth0-react';
import { CompanyInitializer } from '../CompanyInitializer';
import { Company } from '../models/Company';
import Layout from './components/Layout';

// V2 pages
import DashboardPageV2 from './DashboardPage/DashboardPage';
import CompanyPage from './CompanyPage/CompanyPage';
import OfferingsPage from './OfferingsPage/OfferingsPage';
import QuestionsAnswersPage from './QuestionsAnswersPage/QuestionsAnswersPage';
import TrainingHubPage from './TrainingHubPage/TrainingHubPage';

// V2 pages (continued)
import TeamDashboardPage from './TeamDashboardPage/TeamDashboardPage';

// V1 pages reused as-is (will be ported to v2 incrementally)
import CompanyAssessmentsPage from '../pages/CompanyAssessmentsPage';
import OfferingAssessmentsPage from '../pages/OfferingAssessmentsPage';
import NewAssessmentsReportPage from '../pages/NewAssessmentsReportPage';
import MyAssessmentsReportPage from '../pages/MyAssessmentsReportPage';
import ValueReportPage from '../pages/ValueReportPage';
import UserManagement from '../components/UserManagement/UserManagement';
import UserProfile from '../pages/UserProfile';
import AdminPage from '../pages/AdminPage';
import EmailAdminPage from '../pages/EmailAdminPage';
import TermsAndConditionsPage from '../pages/TermsAndConditionsPage';
import RecordPresentationPage from '../pages/RecordPresentationPage';
import GeneralAsksPage from './CompanyPage/GeneralAsksPage';
import V2ContentWrapper from './components/V2ContentWrapper';
import TutorialTour from './Tutorial/TutorialTour';
import PlaybookPage from './PlaybookPage/PlaybookPage';
import PlaybookWrapper from './PlaybookPage/PlaybookWrapper';
import BuildingTheMessage from '../components/Playbook/BuildingTheMessage';
import SalesDeckExplained from '../components/Playbook/BuildingTheMessage/SalesDeckExplained';
import ExampleSalesDeck from '../components/Playbook/BuildingTheMessage/ExampleSalesDeck';
import MessagingExplained from '../components/Playbook/BuildingTheMessage/MessagingExplained';
import AnatomyOfSalesCycle from '../components/Playbook/BuildingTheMessage/AnatomyOfSalesCycle';
import OnboardingTraining from '../components/Playbook/OnboardingTraining';
import TrainingProcessExplained from '../components/Playbook/OnboardingTraining/TrainingProcessExplained';
import ExampleFeedback from '../components/Playbook/OnboardingTraining/ExampleFeedback';
import TrainingSummary from '../components/Playbook/OnboardingTraining/TrainingSummary';
import ExampleSalesMeetings from '../components/Playbook/OnboardingTraining/ExampleSalesMeetings';
import SalesActivity from '../components/Playbook/SalesActivity';
import PlanYourInteractions from '../components/Playbook/SalesActivity/PlanYourInteractions';
import SalesActivityExplained from '../components/Playbook/SalesActivity/SalesActivityExplained';
import Prospecting from '../components/Playbook/SalesActivity/Prospecting';
import IntentionalityCommunication from '../components/Playbook/SalesActivity/IntentionalityCommunication';
import Closing from '../components/Playbook/Closing';
import ProposalProcessExplained from '../components/Playbook/Closing/ProposalProcessExplained';
import ExamplesAndTemplates from '../components/Playbook/Closing/ExamplesAndTemplates';
import ABCCorpSampleProposal from '../components/Playbook/Closing/ABCCorpSampleProposal';
import ABCServicesSampleProposal from '../components/Playbook/Closing/ABCServicesSampleProposal';
import SkillsLeadership from '../components/Playbook/SkillsLeadership';
import CompPlans from '../components/Playbook/SkillsLeadership/CompPlans';
import OrganizationalStructures from '../components/Playbook/SkillsLeadership/OrganizationalStructures';
import ModuleArticlePage from '../pages/ModuleArticlePage';

class HomeV2 extends React.Component {
  onCompanyChange = async (data) => {
    // Full page reload to guarantee clean state — prevents any stale data
    // from the previous company persisting in MobX stores or component state.
    const newCompanyId = data.value;
    const { usersStore } = this.props.rootStore;
    const currentUser = usersStore.currentUser;

    // Verify user has access to the selected company before switching
    if (!currentUser.isAdmin && !(currentUser.companyIds || []).includes(newCompanyId)) {
      console.error('Attempted to switch to unauthorized company:', newCompanyId);
      return;
    }

    // Store selected company in sessionStorage so App.jsx uses it on reload
    sessionStorage.setItem('selectedCompanyId', newCompanyId);
    window.location.reload();
  };

  onAddBusinessLine = async (name) => {
    const { usersStore, companyStore, assessmentsStore, adminStore, modulesStore } = this.props.rootStore;

    const company = Company.create({
      name,
      phone: '',
      streetAddress: '',
      zipCode: '',
      city: '',
      state: '',
    });

    await company.createFromSnapshot({
      name,
      phone: '',
      streetAddress: '',
      zipCode: '',
      city: '',
      state: '',
    });

    await usersStore.currentUser.addUserToCompany(company.id);

    await adminStore.loadEnterprise(usersStore.currentUser.userId);
    await CompanyInitializer(companyStore, assessmentsStore, usersStore, modulesStore, company.id);
  };

  logout = () => {
    const { history } = this.props;
    this.props.auth0.logout({ returnTo: window.location.origin });
  };

  render() {
    const { history, rootStore } = this.props;
    const {
      usersStore,
      companyStore,
      assessmentsStore,
      adminStore,
      modulesStore,
      meetingStore,
      videoPresentationStore,
    } = rootStore;

    const company = companyStore.company;
    const currentUser = usersStore.currentUser;
    const selectedCompanyId = company?.id;

    if (currentUser.disabled) {
      return <div style={{ padding: 40, fontFamily: 'Montserrat', color: '#0784a7' }}>Your account is disabled. Please contact your administrator.</div>;
    }
    if (!currentUser.active) {
      return <div style={{ padding: 40, fontFamily: 'Montserrat', color: '#0784a7' }}>Your account is inactive. Please contact your administrator.</div>;
    }

    const { isAdmin, isSalesMember, isReadOnlyExecutive, isReadOnlySalesManager, isReadOnlySeniorManager } = currentUser;
    const showAssessmentActions = !isSalesMember && !isReadOnlyExecutive && !isReadOnlySalesManager && !isReadOnlySeniorManager;
    const showUserManagement = !isSalesMember && !isReadOnlyExecutive && !isReadOnlySalesManager && !isReadOnlySeniorManager;
    const mode = isSalesMember || isReadOnlyExecutive || isReadOnlySalesManager || isReadOnlySeniorManager ? 'readonly' : undefined;

    return (
      <TutorialTour currentUser={currentUser} history={history}>
        <Layout
          key={selectedCompanyId}
          currentUser={currentUser}
          companyStore={companyStore}
          adminStore={adminStore}
          onCompanyChange={this.onCompanyChange}
          onAddBusinessLine={this.onAddBusinessLine}
          onLogout={this.logout}
          selectedCompanyId={selectedCompanyId}
          history={history}
        >
          <Switch>
          {/* V2 Dashboard */}
          <Route path="/dashboard">
            <DashboardPageV2
              usersStore={usersStore}
              companyStore={companyStore}
              assessmentsStore={assessmentsStore}
              meetingStore={meetingStore}
              history={history}
            />
          </Route>

          {/* V1 pages wrapped in v2 layout with style overrides */}
          <Route path="/team-dashboard">
            <TeamDashboardPage usersStore={usersStore} companyStore={companyStore} assessmentsStore={assessmentsStore} />
          </Route>

          <Route path="/training">
            <TrainingHubPage meetingStore={meetingStore} companyStore={companyStore} />
          </Route>

          <Route path="/record-presentation">
            <RecordPresentationPage
              meetingStore={meetingStore}
              companyStore={companyStore}
              videoPresentationStore={videoPresentationStore}
              assessmentsStore={assessmentsStore}
              usersStore={usersStore}
              mode={mode}
            />
          </Route>

          {/* V2 Company Overview — tabbed card-based page */}
          <Route path="/company">
            <CompanyPage company={company} mode={mode} />
          </Route>

          <Route path="/offerings">
            <OfferingsPage company={company} mode={mode} />
          </Route>
          <Route path="/objection-handling">
            <QuestionsAnswersPage company={company} mode={mode} />
          </Route>
          <Route path="/general-asks">
            <GeneralAsksPage company={company} mode={mode} />
          </Route>

          {/* Assessments */}
          {showAssessmentActions && (
            <Route path="/company-assessments">
              <V2ContentWrapper><CompanyAssessmentsPage usersStore={usersStore} assessmentsStore={assessmentsStore} /></V2ContentWrapper>
            </Route>
          )}
          {showAssessmentActions && (
            <Route path="/offering-assessments">
              <V2ContentWrapper><OfferingAssessmentsPage usersStore={usersStore} assessmentsStore={assessmentsStore} /></V2ContentWrapper>
            </Route>
          )}

          {/* Reports */}
          <Route path="/assessments-report">
            <V2ContentWrapper><NewAssessmentsReportPage usersStore={usersStore} assessmentsStore={assessmentsStore} companyStore={companyStore} /></V2ContentWrapper>
          </Route>
          <Route path="/my-assessments-report">
            <div data-tour="reports-page"><V2ContentWrapper><MyAssessmentsReportPage usersStore={usersStore} assessmentsStore={assessmentsStore} companyStore={companyStore} /></V2ContentWrapper></div>
          </Route>
          <Route path="/value-report/:userId?" children={<V2ContentWrapper><ValueReportPage usersStore={usersStore} assessmentsStore={assessmentsStore} /></V2ContentWrapper>} />

          {/* Playbook — v2 landing page, v1 content pages wrapped for styling */}
          <Route path='/playbook' exact><PlaybookPage /></Route>
          <Route path='/playbook/building-message' exact><PlaybookWrapper><BuildingTheMessage /></PlaybookWrapper></Route>
          <Route path='/playbook/building-message/sales-deck-explained'><PlaybookWrapper><SalesDeckExplained /></PlaybookWrapper></Route>
          <Route path='/playbook/building-message/example-sales-deck'><PlaybookWrapper><ExampleSalesDeck /></PlaybookWrapper></Route>
          <Route path='/playbook/building-message/messaging-explained'><PlaybookWrapper><MessagingExplained /></PlaybookWrapper></Route>
          <Route path='/playbook/building-message/anatomy-sales-cycle'><PlaybookWrapper><AnatomyOfSalesCycle /></PlaybookWrapper></Route>
          <Route path='/playbook/onboarding-training' exact><PlaybookWrapper><OnboardingTraining /></PlaybookWrapper></Route>
          <Route path='/playbook/onboarding-training/training-process-explained'><PlaybookWrapper><TrainingProcessExplained /></PlaybookWrapper></Route>
          <Route path='/playbook/onboarding-training/example-feedback'><PlaybookWrapper><ExampleFeedback /></PlaybookWrapper></Route>
          <Route path='/playbook/onboarding-training/training-summary'><PlaybookWrapper><TrainingSummary /></PlaybookWrapper></Route>
          <Route path='/playbook/onboarding-training/example-sales-meetings'><PlaybookWrapper><ExampleSalesMeetings /></PlaybookWrapper></Route>
          <Route path='/playbook/sales-activity' exact><PlaybookWrapper><SalesActivity /></PlaybookWrapper></Route>
          <Route path='/playbook/sales-activity/plan-your-interactions'><PlaybookWrapper><PlanYourInteractions /></PlaybookWrapper></Route>
          <Route path='/playbook/sales-activity/sales-activity-explained'><PlaybookWrapper><SalesActivityExplained /></PlaybookWrapper></Route>
          <Route path='/playbook/sales-activity/prospecting'><PlaybookWrapper><Prospecting /></PlaybookWrapper></Route>
          <Route path='/playbook/sales-activity/intentionality-communication'><PlaybookWrapper><IntentionalityCommunication /></PlaybookWrapper></Route>
          <Route path='/playbook/closing' exact><PlaybookWrapper><Closing /></PlaybookWrapper></Route>
          <Route path='/playbook/closing/proposal-process-explained'><PlaybookWrapper><ProposalProcessExplained /></PlaybookWrapper></Route>
          <Route path='/playbook/closing/examples-templates'><PlaybookWrapper><ExamplesAndTemplates /></PlaybookWrapper></Route>
          <Route path='/playbook/closing/abc-corp-sample-proposal'><PlaybookWrapper><ABCCorpSampleProposal /></PlaybookWrapper></Route>
          <Route path='/playbook/closing/abc-services-sample-proposal'><PlaybookWrapper><ABCServicesSampleProposal /></PlaybookWrapper></Route>
          <Route path='/playbook/skills-leadership' exact><PlaybookWrapper><SkillsLeadership /></PlaybookWrapper></Route>
          <Route path='/playbook/skills-leadership/comp-plans'><PlaybookWrapper><CompPlans /></PlaybookWrapper></Route>
          <Route path='/playbook/skills-leadership/organizational-structures'><PlaybookWrapper><OrganizationalStructures /></PlaybookWrapper></Route>

          {/* Users & Settings */}
          {showUserManagement && (
            <Route path="/users">
              <V2ContentWrapper><UserManagement usersStore={usersStore} company={company} /></V2ContentWrapper>
            </Route>
          )}
          <Route path="/profile">
            <V2ContentWrapper><UserProfile currentUser={currentUser} editUser={currentUser} companyStore={companyStore} /></V2ContentWrapper>
          </Route>
          <Route path="/terms-conditions"><V2ContentWrapper><TermsAndConditionsPage /></V2ContentWrapper></Route>
          {isAdmin && <Route path="/admin"><V2ContentWrapper><AdminPage adminStore={adminStore} /></V2ContentWrapper></Route>}
          {isAdmin && <Route path="/email-admin"><V2ContentWrapper><EmailAdminPage /></V2ContentWrapper></Route>}

          {/* Dynamic module articles */}
          {modulesStore.allArticles.map((article, i) => (
            <Route path={`${article.path}`} key={`v2_article_${i}`}>
              <V2ContentWrapper><ModuleArticlePage article={article} /></V2ContentWrapper>
            </Route>
          ))}

          {/* Catch-all — redirect any unmatched route to dashboard */}
          <Route>
            <Redirect to="/dashboard" />
          </Route>
          </Switch>
        </Layout>
      </TutorialTour>
    );
  }
}

export default withAuth0(observer(HomeV2));
